import {
	DeleteOutlined,
	DownloadOutlined,
	EditOutlined,
	PlusOutlined,
	ReloadOutlined,
	UndoOutlined,
	UploadOutlined,
} from '@ant-design/icons';
import {
	Alert,
	Button,
	Form,
	Input,
	InputNumber,
	Modal,
	Popconfirm,
	Select,
	Switch,
	Table,
	Tag,
	Upload,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useEffect, useMemo, useState } from 'react';

import {
	SapCodeQuotaAreaDto,
	SapCodeQuotaDto,
	SapCodeQuotaImportErrorDto,
	SapCodeQuotaImportResultDto,
	SapCodeQuotaWriteDto,
} from '../../../../api-client';

type BVQuotasTableProps = {
	loading: boolean;
	quotas: SapCodeQuotaDto[];
	total: number;
	areas: SapCodeQuotaAreaDto[];
	areasLoading: boolean;
	onRefresh: (responsabilityAreaCode?: string, sapCode?: string) => Promise<void>;
	onLoadAreas: () => Promise<void>;
	onSave: (quota: SapCodeQuotaWriteDto, id?: number) => Promise<boolean>;
	saving: boolean;
	onRemove: (id: number) => Promise<void>;
	removing: boolean;
	onReset: (id: number) => Promise<void>;
	resetting: boolean;
	onExport: () => Promise<void>;
	onExportTemplate: () => Promise<void>;
	exporting: boolean;
	onImport: (file: File | Blob) => Promise<void>;
	importing: boolean;
	importResult: SapCodeQuotaImportResultDto | null;
	onClearImportResult: () => void;
};

const emptyForm: SapCodeQuotaWriteDto = {
	sapCode: '',
	responsabilityAreaCode: '',
	maxQuantity: 0,
	active: true,
	note: '',
};

const BVQuotasTable = ({
	loading,
	quotas,
	total,
	areas,
	areasLoading,
	onRefresh,
	onLoadAreas,
	onSave,
	saving,
	onRemove,
	removing,
	onReset,
	resetting,
	onExport,
	onExportTemplate,
	exporting,
	onImport,
	importing,
	importResult,
	onClearImportResult,
}: BVQuotasTableProps) => {
	const [areaFilter, setAreaFilter] = useState<string | undefined>(undefined);
	const [sapCodeFilter, setSapCodeFilter] = useState<string>('');

	const [formOpen, setFormOpen] = useState(false);
	const [editing, setEditing] = useState<SapCodeQuotaDto | null>(null);
	const [form, setForm] = useState<SapCodeQuotaWriteDto>(emptyForm);

	useEffect(() => {
		onRefresh(undefined, undefined);
		onLoadAreas();
	}, []);

	const handleRefresh = async () => {
		const sap = sapCodeFilter.trim();
		await onRefresh(areaFilter, sap ? sap : undefined);
	};

	const openCreate = () => {
		setEditing(null);
		setForm(emptyForm);
		setFormOpen(true);
	};

	const openEdit = (quota: SapCodeQuotaDto) => {
		setEditing(quota);
		setForm({
			sapCode: quota.sapCode,
			responsabilityAreaCode: quota.responsabilityAreaCode,
			maxQuantity: quota.maxQuantity,
			active: quota.active,
			note: quota.note ?? '',
		});
		setFormOpen(true);
	};

	const handleSave = async () => {
		const saved = await onSave(form, editing?.id);
		if (!saved) return;

		setFormOpen(false);
		await handleRefresh();
	};

	const areaOptions = useMemo(
		() =>
			areas.map((a) => ({
				value: a.code,
				label: a.name ? `${a.code} - ${a.name}` : a.code,
			})),
		[areas],
	);

	const columns: ColumnsType<SapCodeQuotaDto> = useMemo(
		() => [
			{
				title: 'Area',
				key: 'area',
				render: (_v, r) =>
					r.responsabilityAreaName
						? `${r.responsabilityAreaCode} - ${r.responsabilityAreaName}`
						: r.responsabilityAreaCode,
			},
			{ title: 'SAP Code', dataIndex: 'sapCode', key: 'sapCode' },
			{
				title: 'Max',
				dataIndex: 'maxQuantity',
				key: 'maxQuantity',
				width: 90,
				align: 'right',
			},
			{
				title: 'Used',
				dataIndex: 'usedQuantity',
				key: 'usedQuantity',
				width: 90,
				align: 'right',
			},
			{
				title: 'Remaining',
				key: 'remainingQuantity',
				width: 110,
				align: 'right',
				// The backend never returns a negative remaining quantity: when the maximum is lowered below the
				// quantity already ordered it comes back as zero, and the row is flagged instead.
				render: (_v, r) =>
					r.remainingQuantity === 0 ? (
						<Tag color="red">0</Tag>
					) : (
						r.remainingQuantity
					),
			},
			{
				title: 'Active',
				key: 'active',
				width: 90,
				render: (_v, r) =>
					r.active ? (
						<Tag color="green">Yes</Tag>
					) : (
						<Tag color="default">No</Tag>
					),
			},
			{
				title: 'Last reset',
				key: 'lastReset',
				render: (_v, r) =>
					r.lastResetAt
						? `${new Date(r.lastResetAt).toLocaleString()}${
								r.lastResetBy ? ` by ${r.lastResetBy}` : ''
						  }`
						: '-',
			},
			{
				title: 'Note',
				dataIndex: 'note',
				key: 'note',
				render: (v?: string | null) => v || '-',
			},
			{
				title: '',
				key: 'actions',
				width: 150,
				align: 'right',
				render: (_v, r) => (
					<div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
						<Button
							icon={<EditOutlined />}
							size="small"
							onClick={() => openEdit(r)}
						/>

						<Popconfirm
							title="Reset the counter? Orders confirmed before now will stop counting for this quota. This cannot be undone."
							okText="Reset"
							cancelText="Cancel"
							onConfirm={async () => {
								await onReset(r.id);
								await handleRefresh();
							}}
						>
							<Button
								icon={<UndoOutlined />}
								size="small"
								loading={resetting}
							/>
						</Popconfirm>

						<Popconfirm
							title="Delete this quota? The sap code goes back to being unlimited for this area. This cannot be undone."
							okText="Delete"
							cancelText="Cancel"
							onConfirm={async () => {
								await onRemove(r.id);
								await handleRefresh();
							}}
						>
							<Button
								icon={<DeleteOutlined />}
								danger
								size="small"
								loading={removing}
							/>
						</Popconfirm>
					</div>
				),
			},
		],
		[removing, resetting, areaFilter, sapCodeFilter],
	);

	const importErrorColumns: ColumnsType<SapCodeQuotaImportErrorDto> = [
		{ title: 'Row', dataIndex: 'rowNumber', key: 'rowNumber', width: 80 },
		{
			title: 'SAP Code',
			dataIndex: 'sapCode',
			key: 'sapCode',
			render: (v?: string | null) => v || '-',
		},
		{
			title: 'Area',
			dataIndex: 'responsabilityAreaCode',
			key: 'area',
			render: (v?: string | null) => v || '-',
		},
		{ title: 'Reason', dataIndex: 'reason', key: 'reason' },
	];

	return (
		<div className="admin-card" style={{ marginTop: 16 }}>
			<div
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					gap: 12,
					flexWrap: 'wrap',
				}}
			>
				<label className="admin-label">{`Quotas (${total})`}</label>

				<div
					style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}
				>
					<Select
						style={{ minWidth: 260 }}
						placeholder="Filter by responsability area"
						options={areaOptions}
						value={areaFilter}
						loading={areasLoading}
						allowClear
						showSearch
						optionFilterProp="label"
						onChange={(value) => setAreaFilter(value)}
					/>

					<Input
						style={{ minWidth: 220 }}
						placeholder="Filter by SAP code (optional)"
						value={sapCodeFilter}
						allowClear
						onChange={(e) => setSapCodeFilter(e.target.value)}
						onPressEnter={handleRefresh}
					/>

					<Button
						icon={<ReloadOutlined />}
						onClick={handleRefresh}
						loading={loading}
					>
						Refresh
					</Button>

					<Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
						New quota
					</Button>

					<Upload
						accept=".xlsx"
						showUploadList={false}
						beforeUpload={async (file) => {
							await onImport(file);
							await handleRefresh();
							return false;
						}}
					>
						<Button icon={<UploadOutlined />} loading={importing}>
							Import
						</Button>
					</Upload>

					<Button
						icon={<DownloadOutlined />}
						onClick={onExport}
						loading={exporting}
					>
						Export
					</Button>

					<Button icon={<DownloadOutlined />} onClick={onExportTemplate}>
						Template
					</Button>
				</div>
			</div>

			{importResult && importResult.skipped > 0 && (
				<Alert
					style={{ marginTop: 12 }}
					type="warning"
					closable
					onClose={onClearImportResult}
					message={`Import report: ${importResult.created} created, ${importResult.updated} updated, ${importResult.skipped} skipped out of ${importResult.totalRows} rows.`}
					description={
						<Table<SapCodeQuotaImportErrorDto>
							style={{ marginTop: 8 }}
							rowKey={(r) => `${r.rowNumber}-${r.sapCode ?? ''}`}
							columns={importErrorColumns}
							dataSource={importResult.errors}
							pagination={{ pageSize: 10 }}
							size="small"
						/>
					}
				/>
			)}

			<Table<SapCodeQuotaDto>
				style={{ marginTop: 12 }}
				rowKey={(r) => r.id}
				loading={loading}
				dataSource={quotas}
				columns={columns}
				pagination={{ pageSize: 20 }}
			/>

			<Modal
				title={editing ? 'Edit quota' : 'New quota'}
				visible={formOpen}
				onCancel={() => setFormOpen(false)}
				onOk={handleSave}
				okText="Save"
				confirmLoading={saving}
				okButtonProps={{
					disabled: !form.sapCode.trim() || !form.responsabilityAreaCode,
				}}
			>
				<Form layout="vertical">
					<Form.Item label="Responsability area" required>
						<Select
							options={areaOptions}
							value={form.responsabilityAreaCode || undefined}
							loading={areasLoading}
							showSearch
							optionFilterProp="label"
							placeholder="Select an area"
							onChange={(value) =>
								setForm({ ...form, responsabilityAreaCode: value })
							}
						/>
					</Form.Item>

					<Form.Item
						label="SAP code"
						required
						help="The code must exist on a product size, otherwise the save is refused."
					>
						<Input
							value={form.sapCode}
							onChange={(e) => setForm({ ...form, sapCode: e.target.value })}
						/>
					</Form.Item>

					<Form.Item label="Max quantity" required>
						<InputNumber
							min={0}
							style={{ width: '100%' }}
							value={form.maxQuantity}
							onChange={(value) =>
								setForm({ ...form, maxQuantity: value ?? 0 })
							}
						/>
					</Form.Item>

					<Form.Item label="Active">
						<Switch
							checked={form.active}
							onChange={(checked) => setForm({ ...form, active: checked })}
						/>
					</Form.Item>

					<Form.Item label="Note">
						<Input.TextArea
							rows={2}
							value={form.note ?? ''}
							onChange={(e) => setForm({ ...form, note: e.target.value })}
						/>
					</Form.Item>
				</Form>
			</Modal>
		</div>
	);
};

export default BVQuotasTable;
